import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import styled from "styled-components"

import Flex from "../atoms/Flex"
import Card from "../atoms/Card"
import { Title } from "../atoms/Typography"
import Icon from "../atoms/Icon"
import RichText from "../atoms/RichText"
import Button from "../atoms/Button"

const StyledCard = styled(Card)`
  transition: opacity 0.3s cubic-bezier(0.17, 0.67, 0.58, 0.84);
  &:hover {
    opacity: ${props => (props.active ? 1 : 0.7)};
  }
`

const InteractiveInfoCard = ({
  idx,
  title,
  icon,
  align = "left",
  card,
  iconLocation,
  iconSize,
  paragraphRichText,
  image,
  active,
  onClick,
  onMouseEnter,
  button,
  showTitle,
  columnsDesktop,
  columnsMobile,
  ...rest
}) => {
  const textAlignValue = (align || "left").trim()
  const isTextCentered = textAlignValue === "center"
  const isTextRight = textAlignValue === "right"

  const isIconLeft = iconLocation === "left"

  // When icon is on top (column layout), aligns the column horizontally to match text alignment
  const containerAlignItems = !isIconLeft
    ? isTextCentered
      ? "center"
      : isTextRight
        ? "flex-end"
        : null
    : null

  return (
    <StyledCard
      height="100%"
      {...rest}
      {...card}
      active={active}
      boxShadow={active ? card?.boxShadow : "none"}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      style={{
        cursor: onClick || onMouseEnter ? "pointer" : "default",
        zIndex: active ? 101 : 100,
      }}
    >
      <Flex
        p={card?.useCard ? 6 : 0}
        alignItems={containerAlignItems}
        flexDirection={isIconLeft ? "row" : "column"}
      >
        {icon && (
          <Icon
            flexShrink={0}
            {...icon}
            mr={isIconLeft ? 4 : 0}
            mb={isIconLeft ? 0 : 4}
            width={iconSize ? `${iconSize}px` : 2}
            height={iconSize ? `${iconSize}px` : 2}
          />
        )}
        <Flex
          flexDirection={{
            _: columnsMobile === "two" ? "row" : "column",
            lg: columnsDesktop === "two" ? "row" : "column",
          }}
          style={{ columnGap: "32px" }}
        >
          {showTitle && (
            <Title
              flex={1}
              textAlign={
                isTextCentered ? "center" : isTextRight ? "right" : null
              }
              variant="h3"
              mb={{
                _: columnsMobile === "two" ? null : 3,
                lg: columnsDesktop === "two" ? null : 3,
              }}
            >
              {title}
            </Title>
          )}
          <Flex flexDirection="column" flex={1}>
            {paragraphRichText?.content && (
              <RichText
                textAlign={
                  isTextCentered ? "center" : isTextRight ? "right" : null
                }
                {...paragraphRichText}
              />
            )}
            {button && (
              <Button
                aria-label="Button in interactive info card"
                {...button}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </StyledCard>
  )
}

export default InteractiveInfoCard

InteractiveInfoCard.strapiProps = {
  title: PropTypes.string.isRequired,
  paragraph: PropTypes.string,
  icon: PropTypes.shape(Icon.strapiProps),
  paragraphRichText: PropTypes.object,
}

InteractiveInfoCard.propTypes = {
  ...InteractiveInfoCard.strapiProps,
  ...Card.propTypes,
  align: PropTypes.oneOf(["left", "center", "right"]).isRequired,
  iconLocation: PropTypes.oneOf(["left", "top"]),
}

export const query = graphql`
  fragment InteractiveInfoCard on STRAPI__COMPONENT_MOLECULES_INTERACTIVE_INFO_CARD {
    title
    icon {
      ...Icon
    }
    paragraphRichText {
      ...RichText
    }
    button {
      ...Button
    }
    image {
      ...ImageWithConstrainedLayout
    }
  }
`
